import React, { useMemo } from 'react';
import { 
  Users, 
  Bus, 
  CreditCard, 
  TrendingUp, 
  ArrowUpRight, 
  ArrowDownRight,
  Calendar,
  MapPin,
  Clock,
  Wallet,
  UserCheck,
  Route,
  Activity,
  ChevronLeft,
  Shield
} from 'lucide-react';
import { Card, Badge } from '../components/ui/index.jsx';
import { useGet } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

import { motion } from 'framer-motion';
import { 
  AreaChart, Area, 
  BarChart, Bar, 
  PieChart, Pie, Cell,
  ResponsiveContainer, 
  XAxis, YAxis, 
  CartesianGrid, 
  Tooltip,
  Legend
} from 'recharts';
import { Link } from 'react-router-dom';

// Animation variants
const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.08 }
  }
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } }
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, trend, trendValue, color, delay = 0 }) => (
  <motion.div variants={item}>
    <Card className="p-4 relative overflow-hidden group hover:shadow-lg hover:shadow-slate-100 transition-all duration-300">
      <div className="flex items-start justify-between">
        <div className="flex flex-col">
          <span className="text-[10px]  text-slate-400 uppercase tracking-widest mb-1">{title}</span>
          <h3 className="text-2xl  text-slate-800">{value}</h3>
          {trend && (
            <div className="flex items-center gap-1.5 mt-2">
              
              
            </div>
          )}
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${color || 'bg-primary/10 text-primary'}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      
    </Card>
  </motion.div>
);

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 backdrop-blur-sm border border-slate-100 rounded-lg px-3 py-2 shadow-lg text-right" dir="rtl">
        <p className="text-[10px]  text-slate-400 mb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} className="text-xs " style={{ color: p.color }}>
            {p.name}: {p.value.toLocaleString()} د.م
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Gender Tooltip for distribution charts
const GenderTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white/95 backdrop-blur-sm border border-slate-100 rounded-lg px-3 py-2 shadow-lg text-right min-w-[140px]" dir="rtl">
          <p className="text-xs font-bold text-slate-800 mb-2 border-b border-slate-50 pb-1">{label || data.name}</p>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#3b82f6]" />
                <span className="text-[10px] text-slate-500">ذكور</span>
              </div>
              <span className="text-[10px] font-medium text-slate-700">{data.male} ({data.male_percentage}%)</span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-1.5">
                <div className="w-1.5 h-1.5 rounded-full bg-[#f43f5e]" />
                <span className="text-[10px] text-slate-500">إناث</span>
              </div>
              <span className="text-[10px] font-medium text-slate-700">{data.female} ({data.female_percentage}%)</span>
            </div>
            <div className="flex items-center justify-between gap-4 pt-1.5 border-t border-slate-50 mt-1">
              <span className="text-[10px] font-bold text-slate-800">المجموع</span>
              <span className="text-[10px] font-bold text-primary">{(data.value || data.students).toLocaleString()} طالب</span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

const COLORS = ['#eea508', '#3b82f6', '#10b981', '#8b5cf6', '#f43f5e', '#06b6d4'];

const DashboardHome = () => {
    const { hasPermission } = useAuth();
    const canSeeStudents = hasPermission('students', 'read');
    const canSeeBuses = hasPermission('buses', 'read');
    const canSeeDrivers = hasPermission('drivers', 'read');
    const canSeePayments = hasPermission('payments', 'read');
    const canSeeRegions = hasPermission('regions', 'read');
    const canSeeClasses = hasPermission('school-classes', 'read');
    const canSeeInstitutions = hasPermission('institutions', 'read');

    if (!hasPermission('dashboard', 'read')) {
        return (
            <Card className="p-10 text-center">
                <Shield className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <h3 className="text-sm  text-slate-800">صلاحيات غير كافية</h3>
                <p className="text-xs text-slate-500 mt-1">لا يمكنك الوصول للوحة التحكم، هذا القسم مخصص للمصرح لهم فقط.</p>
            </Card>
        );
    }

    const { data: stats, loading } = useGet('/dashboard/stats');


    if (loading && !stats) {
        return (
            <div className="flex items-center justify-center min-h-[60vh]">
                <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    const totalStudents = stats?.totalStudents || 0;
    const totalBuses = stats?.totalBuses || 0;
    const totalDrivers = stats?.totalDrivers || 0;
    const totalRegions = stats?.totalRegions || 0;
    
    const totalRevenue = stats?.totalRevenue || 0;
    const avgPerStudent = stats?.avgPerStudent || 0;

    const monthlyData = stats?.monthlyData || [];
    const studentsByRegion = stats?.studentsByRegion || [];
    const studentsPerBus = stats?.studentsPerBus || [];
    const studentsByClass = stats?.studentsByClass || [];
    const studentsByInstitution = stats?.studentsByInstitution || [];
    const studentsByInstitutionGender = stats?.studentsByInstitutionGender || [];
    const studentsByGroup = stats?.studentsByGroup || [];
    const studentsByStatus = stats?.studentsByStatus || [];
    const activeYear = stats?.activeYear || null;
    const payments = stats?.recentPayments || [];

    return (
        <motion.div 
          className="space-y-6"
          variants={container}
          initial="hidden"
          animate="show"
        >
            {/* Page Header */}
            <motion.div variants={item} className="flex flex-col md:flex-row md:items-end justify-between gap-3">
                <div>
                    <h2 className="text-xl  text-slate-800">لوحة التحكم</h2>
                    <p className="text-[11px] text-slate-400 leading-none mt-1">نظرة شاملة على أداء النقل المدرسي</p>
                </div>
                {activeYear && (
                  <div className="flex items-center gap-2 bg-primary/5 text-primary px-3 py-1.5 rounded-lg border border-primary/10">
                    <Calendar className="w-3.5 h-3.5" />
                    <span className="text-xs ">{activeYear.name}</span>
                    <Badge variant="primary">نشطة</Badge>
                  </div>
                )}
            </motion.div>

            {/* Stats Grid */}
            <motion.div variants={container} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {canSeeStudents && (
                    <StatCard 
                        title="إجمالي الطلاب" 
                        value={totalStudents} 
                        icon={Users} 
                        trend="up" 
                        trendValue="+12%" 
                        color="bg-blue-50 text-blue-600"
                    />
                )}
                {canSeeBuses && (
                    <StatCard 
                        title="الحافلات النشطة" 
                        value={totalBuses} 
                        icon={Bus} 
                        color="bg-primary/10 text-primary"
                    />
                )}
                {canSeeDrivers && (
                    <StatCard 
                        title="السائقين" 
                        value={totalDrivers} 
                        icon={UserCheck} 
                        color="bg-emerald-50 text-emerald-600"
                    />
                )}
                {canSeePayments && (
                    <StatCard 
                        title="التحصيلات المالية" 
                        value={`${totalRevenue.toLocaleString()} د.م`} 
                        icon={Wallet} 
                        trend="up" 
                        trendValue="+5%" 
                        color="bg-violet-50 text-violet-600"
                    />
                )}
            </motion.div>

            {/* Secondary Stats */}
            <motion.div variants={item} className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {canSeeRegions && (
                    <Card className="p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center">
                            <MapPin className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-lg  text-slate-800">{totalRegions}</p>
                            <p className="text-[10px] text-slate-400 ">المناطق</p>
                        </div>
                    </Card>
                )}
                {(canSeeStudents && canSeeBuses) && (
                    <Card className="p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-cyan-50 text-cyan-600 flex items-center justify-center">
                            <Route className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-lg  text-slate-800">{totalBuses > 0 ? Math.round(totalStudents / totalBuses) : 0}</p>
                            <p className="text-[10px] text-slate-400 ">متوسط ط/حافلة</p>
                        </div>
                    </Card>
                )}
                {canSeePayments && (
                    <Card className="p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-rose-50 text-rose-600 flex items-center justify-center">
                            <CreditCard className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-lg  text-slate-800">{payments?.length || 0}</p>
                            <p className="text-[10px] text-slate-400 ">عملية دفع</p>
                        </div>
                    </Card>
                )}
                {(canSeeStudents && canSeePayments) && (
                    <Card className="p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                            <Activity className="w-4 h-4" />
                        </div>
                        <div>
                            <p className="text-lg  text-slate-800">{avgPerStudent.toLocaleString()} د.م</p>
                            <p className="text-[10px] text-slate-400 ">متوسط/طالب</p>
                        </div>
                    </Card>
                )}
            </motion.div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                {/* Revenue Chart - Area */}
                {canSeePayments && (
                    <motion.div variants={item} className="lg:col-span-2">
                        <Card className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm  text-slate-800">تحليل الإيرادات الشهرية</h3>
                                    <p className="text-[10px] text-slate-400 mt-0.5">تتبع التحصيلات المالية على مدار السنة</p>
                                </div>
                                <div className="flex items-center gap-1.5 text-[10px]  text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                                    <TrendingUp className="w-3 h-3" />
                                    مستقر
                                </div>
                            </div>
                            <div className="h-[220px] w-full" dir="ltr">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={monthlyData} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="revenueGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#eea508" stopOpacity={0.15}/>
                                                <stop offset="95%" stopColor="#eea508" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#94a3b8' }} 
                                        />
                                        <YAxis 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#94a3b8' }} 
                                        />
                                        <Tooltip content={<CustomTooltip />} />
                                        <Area 
                                            type="monotone" 
                                            dataKey="revenue" 
                                            name="الإيرادات"
                                            stroke="#eea508" 
                                            strokeWidth={2.5}
                                            fill="url(#revenueGrad)" 
                                            dot={false}
                                            activeDot={{ r: 5, fill: '#eea508', stroke: '#fff', strokeWidth: 2 }}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </Card>
                    </motion.div>
                )}

                {/* Students by Region - Pie */}
                {canSeeRegions && (
                    <motion.div variants={item}>
                        <Card className="p-4 h-full">
                            <div className="mb-3">
                                <h3 className="text-sm  text-slate-800">توزيع الطلاب</h3>
                                <p className="text-[10px] text-slate-400 mt-0.5">حسب المنطقة</p>
                            </div>
                            {studentsByRegion.length > 0 ? (
                            <>
                                <div className="h-[160px] w-full" dir="ltr">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={studentsByRegion}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={40}
                                                outerRadius={65}
                                                paddingAngle={3}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {studentsByRegion.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip content={<GenderTooltip />} />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="flex flex-wrap gap-2 mt-2" dir="rtl">
                                    {studentsByRegion.slice(0, 4).map((entry, idx) => (
                                        <div key={idx} className="flex items-center gap-1.5">
                                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                            <span className="text-[10px] text-slate-500 ">{entry.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </>
                            ) : (
                            <div className="h-[180px] flex items-center justify-center text-slate-400 text-xs">لا توجد بيانات</div>
                            )}
                        </Card>
                    </motion.div>
                )}
            </div>

            {/* Students by Status Row */}
            <div className="grid grid-cols-1 gap-5">
                {canSeeStudents && (
                    <motion.div variants={item}>
                        <Card className="p-4">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm font-bold text-slate-800">توزيع الطلاب حسب الحالة</h3>
                                    <p className="text-[10px] text-slate-400 mt-0.5">تصنيف الطلاب بناءً على الوضع الاجتماعي والصحي</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                <div className="h-[220px] w-full" dir="ltr">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <PieChart>
                                            <Pie
                                                data={studentsByStatus.map(s => ({ name: s.name, value: s.students }))}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                paddingAngle={5}
                                                dataKey="value"
                                                stroke="none"
                                            >
                                                {studentsByStatus.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                                ))}
                                            </Pie>
                                            <Tooltip 
                                                formatter={(value) => [`${value} طالب`]}
                                                contentStyle={{ borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '11px' }}
                                            />
                                        </PieChart>
                                    </ResponsiveContainer>
                                </div>
                                <div className="grid grid-cols-2 gap-3" dir="rtl">
                                    {studentsByStatus.map((item, idx) => (
                                        <div key={idx} className="flex flex-col p-3 rounded-xl bg-slate-50/50 border border-slate-100 hover:bg-slate-50 transition-colors">
                                            <div className="flex items-center gap-2 mb-1">
                                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                                                <span className="text-[11px] font-medium text-slate-600">{item.name}</span>
                                            </div>
                                            <p className="text-lg font-bold text-slate-800 mr-4.5">{item.students}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                )}
            </div>

            {/* Middle Section - New Chart for Classes, Institutions, and Buses */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                 {/* Students per Class - Bar */}
                 {canSeeClasses && (
                    <motion.div variants={item}>
                        <Card className="p-4">
                            <div className="mb-4">
                                <h3 className="text-sm  text-slate-800">الطلاب لكل قسم</h3>
                                <p className="text-[10px] text-slate-400 mt-0.5">توزيع الطلاب حسب المستويات الدراسية</p>
                            </div>
                            {studentsByClass.length > 0 ? (
                            <div className="h-[200px] w-full" dir="ltr">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={studentsByClass} margin={{ top: 0, right: 5, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={true} horizontal={false} />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#64748b' }} 
                                        />
                                        <YAxis 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#94a3b8' }} 
                                        />
                                        <Tooltip content={<GenderTooltip />} />
                                        <Legend 
                                             verticalAlign="top" 
                                             align="right" 
                                             iconSize={8} 
                                             iconType="circle"
                                             wrapperStyle={{ fontSize: '9px', paddingBottom: '10px' }}
                                             formatter={(value) => (value === 'male' ? 'ذكور' : 'إناث')}
                                         />
                                        <Bar dataKey="male" name="male" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} barSize={25} />
                                        <Bar dataKey="female" name="female" stackId="a" fill="#f43f5e" radius={[4, 4, 0, 0]} barSize={25} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            ) : (
                            <div className="h-[200px] flex items-center justify-center text-slate-400 text-xs">لا توجد بيانات</div>
                            )}
                        </Card>
                    </motion.div>
                 )}

                 {/* Students per Institution - Bar */}
                 {canSeeInstitutions && (
                    <motion.div variants={item}>
                        <Card className="p-4">
                            <div className="mb-4">
                                <h3 className="text-sm  text-slate-800">الطلاب لكل مؤسسة</h3>
                                <p className="text-[10px] text-slate-400 mt-0.5">توزيع الطلاب حسب المؤسسات التعليمية</p>
                            </div>
                            {studentsByInstitution.length > 0 ? (
                            <div className="h-[200px] w-full" dir="ltr">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={studentsByInstitution} margin={{ top: 0, right: 5, left: -20, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={true} horizontal={false} />
                                        <XAxis 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#64748b' }} 
                                        />
                                        <YAxis 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#94a3b8' }} 
                                        />
                                        <Tooltip 
                                            formatter={(value) => [`${value} طالب`]}
                                            contentStyle={{ borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '11px' }}
                                        />
                                        <Bar 
                                            dataKey="students" 
                                            fill="#10b981" 
                                            radius={[4, 4, 0, 0]} 
                                            barSize={30}
                                            name="الطلاب"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            ) : (
                            <div className="h-[200px] flex items-center justify-center text-slate-400 text-xs">لا توجد بيانات</div>
                            )}
                        </Card>
                    </motion.div>
                 )}
 
                 {/* Students by Institution Gender (Stacked Bar) */}
                 {canSeeInstitutions && studentsByInstitutionGender.length > 0 && (
                     <motion.div variants={item} className="lg:col-span-1">
                         <Card className="p-4">
                             <div className="mb-4">
                                 <h3 className="text-sm  text-slate-800">توزيع الذكور والإناث</h3>
                                 <p className="text-[10px] text-slate-400 mt-0.5">حسب المؤسسات التعليمية</p>
                             </div>
                             <div className="h-[200px] w-full" dir="ltr">
                                 <ResponsiveContainer width="100%" height="100%">
                                     <BarChart 
                                         data={studentsByInstitutionGender} 
                                         margin={{ top: 0, right: 5, left: -20, bottom: 0 }}
                                     >
                                         <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={true} horizontal={false} />
                                         <XAxis 
                                             dataKey="name" 
                                             axisLine={false} 
                                             tickLine={false} 
                                             tick={{ fontSize: 9, fill: '#64748b' }} 
                                         />
                                         <YAxis 
                                             axisLine={false} 
                                             tickLine={false} 
                                             tick={{ fontSize: 9, fill: '#94a3b8' }} 
                                         />
                                         <Tooltip 
                                             contentStyle={{ borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '10px' }}
                                             formatter={(value, name, props) => {
                                                 const percentage = name === 'male' ? props.payload.male_percentage : props.payload.female_percentage;
                                                 return [`${value} (${percentage}%)`, name === 'male' ? 'ذكور' : 'إناث'];
                                             }}
                                         />
                                         <Legend 
                                             verticalAlign="top" 
                                             align="right" 
                                             iconSize={8} 
                                             iconType="circle"
                                             wrapperStyle={{ fontSize: '9px', paddingBottom: '10px' }}
                                             formatter={(value) => (value === 'male' ? 'ذكور' : 'إناث')}
                                         />
                                         <Bar dataKey="male" name="male" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} barSize={20} />
                                         <Bar dataKey="female" name="female" stackId="a" fill="#f43f5e" radius={[4, 4, 0, 0]} barSize={20} />
                                     </BarChart>
                                 </ResponsiveContainer>
                             </div>
                         </Card>
                     </motion.div>
                 )}

                  {/* Students per Group - Bar */}
                  {studentsByGroup?.length > 0 && (
                     <motion.div variants={item}>
                         <Card className="p-4">
                             <div className="mb-4">
                                 <h3 className="text-sm  text-slate-800">الطلاب حسب المجموعة</h3>
                                 <p className="text-[10px] text-slate-400 mt-0.5">توزيع الطلاب على المجموعات المسجلة</p>
                             </div>
                             <div className="h-[200px] w-full" dir="ltr">
                                 <ResponsiveContainer width="100%" height="100%">
                                     <BarChart data={studentsByGroup} margin={{ top: 0, right: 5, left: -20, bottom: 0 }}>
                                         <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={true} horizontal={false} />
                                         <XAxis 
                                             dataKey="name" 
                                             axisLine={false} 
                                             tickLine={false} 
                                             tick={{ fontSize: 10, fill: '#64748b' }} 
                                         />
                                         <YAxis 
                                             axisLine={false} 
                                             tickLine={false} 
                                             tick={{ fontSize: 10, fill: '#94a3b8' }} 
                                         />
                                         <Tooltip 
                                             formatter={(value) => [`${value} طالب`]}
                                             contentStyle={{ borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '11px' }}
                                         />
                                         <Bar 
                                             dataKey="students" 
                                             fill="#8b5cf6" 
                                             radius={[4, 4, 0, 0]} 
                                             barSize={30}
                                             name="الطلاب"
                                         />
                                     </BarChart>
                                 </ResponsiveContainer>
                             </div>
                         </Card>
                     </motion.div>
                  )}

                                  {/* Students per Bus - Bar */}
                {canSeeBuses && (
                    <motion.div variants={item}>
                        <Card className="p-4 h-full">
                            <div className="mb-4">
                                <h3 className="text-sm  text-slate-800">الطلاب لكل حافلة</h3>
                                <p className="text-[10px] text-slate-400 mt-0.5">أكثر الحافلات ازدحاماً</p>
                            </div>
                            {studentsPerBus.length > 0 ? (
                            <div className="h-[200px] w-full" dir="ltr">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={studentsPerBus} layout="vertical" margin={{ top: 0, right: 5, left: 0, bottom: 0 }}>
                                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                                        <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#94a3b8' }} />
                                        <YAxis 
                                            type="category" 
                                            dataKey="name" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{ fontSize: 10, fill: '#64748b' }} 
                                            width={70}
                                        />
                                        <Tooltip 
                                            formatter={(value) => [`${value} طالب`]}
                                            contentStyle={{ borderRadius: '8px', border: '1px solid #f1f5f9', fontSize: '11px' }}
                                        />
                                        <Bar 
                                            dataKey="students" 
                                            fill="#eea508" 
                                            radius={[0, 4, 4, 0]} 
                                            barSize={16}
                                            name="الطلاب"
                                        />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            ) : (
                            <div className="h-[200px] flex items-center justify-center text-slate-400 text-xs">لا توجد بيانات</div>
                            )}
                        </Card>
                    </motion.div>
                )}
            </div>

            {/* Bottom Section - Recent Payments */}
            <div className="grid grid-cols-1 gap-5">
                {/* Recent Payments */}
                {canSeePayments && (
                    <motion.div variants={item}>
                        <Card className="p-4 h-full">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h3 className="text-sm  text-slate-800">آخر التحصيلات</h3>
                                    <p className="text-[10px] text-slate-400 mt-0.5">أحدث عمليات الدفع المسجلة</p>
                                </div>
                                <Link to="/dashboard/payments" className="flex items-center gap-1 text-[10px]  text-primary hover:text-primary-dark transition-colors">
                                    عرض الكل <ChevronLeft className="w-3 h-3" />
                                </Link>
                            </div>

                            <div className="space-y-2">
                                {payments?.slice(0, 5).map((payment, i) => (
                                    <motion.div 
                                        key={payment.id || i} 
                                        className="flex items-center justify-between p-3 rounded-lg border border-slate-50 bg-slate-50/30 hover:bg-slate-50/80 transition-all group"
                                        initial={{ opacity: 0, x: 10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: i * 0.05 }}
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="w-9 h-9 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center justify-center text-emerald-500 group-hover:scale-105 transition-transform">
                                                <ArrowDownRight className="w-4 h-4" />
                                            </div>
                                            <div>
                                                <p className="text-xs  text-slate-800">{payment.student?.full_name || 'غير محدد'}</p>
                                                <div className="flex items-center gap-2 mt-0.5">
                                                    <span className="text-[9px]  text-slate-400 flex items-center gap-0.5">
                                                        <Calendar className="w-2.5 h-2.5" /> {new Date(payment.payment_date).toLocaleDateString('ar-MA')}
                                                    </span>
                                                    <Badge variant="primary">{payment.payment_mode_details?.name || payment.payment_mode || '--'}</Badge>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="text-left">
                                            <p className="text-sm  text-slate-800">{parseFloat(payment.amount).toLocaleString()} د.م</p>
                                            <div className="flex items-center gap-1 text-[9px]  text-emerald-600 justify-end">
                                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> مكتمل
                                            </div>
                                        </div>
                                    </motion.div>
                                ))}
                                {(!payments || payments.length === 0) && (
                                    <div className="text-center py-8 text-slate-400 text-xs">لا توجد عمليات دفع مسجلة بعد</div>
                                )}
                            </div>
                        </Card>
                    </motion.div>
                )}
            </div>
        </motion.div>
    );
};

export default DashboardHome;
